<template>
  <div class="main-image-edit">
    <!-- 로딩 중일 때 표시 -->
    <div v-if="loading" class="loading-container">
      <b-spinner variant="primary"></b-spinner>
      <p>{{ progressMessage || '대표 이미지 작업 중...' }}</p>
    </div>

    <!-- 이미지 편집 화면 -->
    <div class="image-editor-layout">
      <!-- 좌측: 이미지 선택 패널 -->
      <div class="image-selector-panel">
        <div class="panel-header">
          <h5 class="panel-title">이미지 선택</h5>
          <b-button variant="outline-primary" size="sm" @click="showImageUpload" title="이미지 업로드">
            <i class="las la-upload"></i>
          </b-button>
        </div>

        <!-- 이미지 탭 -->
        <div class="image-tabs">
          <b-nav tabs>
            <b-nav-item :active="activeTab === 'processed'" @click="activeTab = 'processed'">작업이미지</b-nav-item>
            <b-nav-item :active="activeTab === 'main'" @click="activeTab = 'main'">메인이미지</b-nav-item>
            <b-nav-item :active="activeTab === 'option'" @click="activeTab = 'option'">옵션이미지</b-nav-item>
            <b-nav-item :active="activeTab === 'detail'" @click="activeTab = 'detail'">상세이미지</b-nav-item>
          </b-nav>
        </div>

        <!-- 이미지 목록 -->
        <div class="image-gallery">
          <!-- 작업 완료된 이미지 탭 -->
          <div v-if="activeTab === 'processed'" class="image-grid">
            <div v-if="processedImages.length > 0">
              <div v-for="(image, index) in processedImages" :key="`processed-${index}`" class="image-item"
                :class="{ selected: selectedImageType === 'processed' && selectedImageIndex === index }"
                @click="selectProcessedImage(index, image)">
                <img :src="image.url" alt="작업된 이미지" class="img-fluid">
                <div class="image-overlay">
                  <i class="las la-check"></i>
                </div>
                <div class="image-label">
                  <span class="text-removed-badge" v-if="image.textRemoved">텍스트 제거됨</span>
                  <span v-else>작업됨</span>
                </div>
              </div>
            </div>
            <div v-else class="empty-gallery">
              <i class="las la-images"></i>
              <p>글자 삭제 작업이 완료된 이미지가 없습니다</p>
              <p class="small text-muted">이미지를 선택한 후 '글자 삭제' 버튼을 눌러 작업을 진행해보세요</p>
            </div>
          </div>

          <!-- 메인 이미지 탭 -->
          <div v-if="activeTab === 'main'" class="image-grid">
            <div v-if="mainImages.length > 0">
              <div v-for="(image, index) in mainImages" :key="`main-${index}`" class="image-item"
                :class="{ selected: selectedImageType === 'main' && selectedImageIndex === index }"
                @click="selectImage('main', index, image)">
                <img :src="image" alt="메인 이미지" class="img-fluid">
                <div class="image-overlay">
                  <i class="las la-check"></i>
                </div>
                <div class="image-label">메인</div>
              </div>
            </div>
            <div v-else class="empty-gallery">
              <i class="las la-images"></i>
              <p>메인 이미지가 없습니다</p>
              <b-button variant="outline-primary" size="sm" @click="fetchImagesFrom1688">
                이미지 불러오기
              </b-button>
            </div>
          </div>

          <!-- 옵션 이미지 탭 -->
          <div v-if="activeTab === 'option'" class="image-grid">
            <div v-if="optionImages.length > 0">
              <div v-for="(image, index) in optionImages" :key="`option-${index}`" class="image-item"
                :class="{ selected: selectedImageType === 'option' && selectedImageIndex === index }"
                @click="selectImage('option', index, image.url)">
                <img :src="image.url" alt="옵션 이미지" class="img-fluid">
                <div class="image-overlay">
                  <i class="las la-check"></i>
                </div>
                <div class="image-label">{{ image.label || '옵션' }}</div>
              </div>
            </div>
            <div v-else class="empty-gallery">
              <i class="las la-images"></i>
              <p>옵션 이미지가 없습니다</p>
            </div>
          </div>

          <!-- 상세 이미지 탭 -->
          <div v-if="activeTab === 'detail'" class="image-grid">
            <div v-if="detailImages.length > 0">
              <div v-for="(image, index) in detailImages" :key="`detail-${index}`" class="image-item"
                :class="{ selected: selectedImageType === 'detail' && selectedImageIndex === index }"
                @click="selectImage('detail', index, image)">
                <img :src="image" alt="상세 이미지" class="img-fluid">
                <div class="image-overlay">
                  <i class="las la-check"></i>
                </div>
                <div class="image-label">상세</div>
              </div>
            </div>
            <div v-else class="empty-gallery">
              <i class="las la-images"></i>
              <p>상세 이미지가 없습니다</p>
            </div>
          </div>
        </div>

        <!-- 이미지 편집 옵션 -->
        <div class="image-options">
          <!-- 현재 선택된 메인 이미지 미리보기 -->
          <div class="current-image-preview mb-3">
            <h6 class="options-title">현재 메인 이미지</h6>
            <div class="square-image-container" @click="showFullscreenPreview" v-if="currentImageUrl">
              <img :src="currentImageUrl" alt="현재 메인 이미지" class="square-preview-image">
              <div class="preview-tooltip">미리 보기</div>
            </div>
            <div v-else class="small-placeholder">이미지 없음</div>
          </div>
          
          <!-- 이미지 편집 버튼 영역 -->
          <div class="image-actions-panel mb-3">
            <!-- 글자 삭제 버튼 (AI 처리로 이름 변경) -->
            <b-button variant="primary" size="sm" class="w-100 mb-2" @click="removeTextFromImage">
              <i class="las la-magic"></i> AI 처리
            </b-button>
          </div>
        </div>
      </div>

      <!-- 우측: 이미지 편집기 -->
      <div class="editor-main-panel">
        <!-- 이미지 표시 영역 -->
        <div class="editor-wrapper">
          <!-- 이미지 작업 전: 일반 이미지 뷰어 -->
          <div v-if="!showEditor && !isRemovingText" class="simple-image-viewer">
            <img :src="currentImageUrl" alt="메인 이미지" class="main-preview-image" v-if="currentImageUrl">
            <div v-else class="no-image-placeholder">
              <i class="las la-image"></i>
              <p>선택된 이미지가 없습니다</p>
            </div>
            <!-- 이미지 위에 떠 있는 편집 버튼 (이미지가 있을 때만 표시) -->
            <div v-if="currentImageUrl" class="floating-edit-button">
              <b-button variant="primary" size="sm" @click="toggleEditor">
                <i class="las la-edit"></i> 편집하기
              </b-button>
            </div>
          </div>
          
          <!-- 이미지 작업 후: iframe 편집기 (requestId가 있을 때만 표시) -->
          <div v-if="showEditor && !isRemovingText && editorRequestId" class="image-container-full" style="width:100%; height:100%;">
            <ImageEditor 
              ref="imageEditor" 
              :requestId="editorRequestId" 
              :imageUrl="editorImageUrl" 
              imageType="main"
              style="width:100%; height:100%;"
              @image-edited="handleEditorImageEdited"
              @error="handleEditorError" 
            />
            <!-- iframe 내부의 저장 버튼이 이미 있어 추가 버튼 제거함 -->
          </div>
          
          <!-- requestId가 없을 때 표시할 메시지 -->
          <div v-if="showEditor && !isRemovingText && !editorRequestId" class="image-container">
            <div class="editor-error-message">
              <i class="las la-exclamation-circle"></i>
              <h5>편집기를 로드할 수 없습니다</h5>
              <p>이미지 번역 요청 ID를 얻지 못했습니다.</p>
              <b-button variant="primary" size="sm" @click="retryTextRemoval" class="mt-3">
                <i class="las la-sync"></i> 다시 시도
              </b-button>
            </div>
          </div>

          <!-- 로딩 오버레이 -->
          <div v-if="isRemovingText" class="loading-overlay">
            <div class="loading-content">
              <b-spinner variant="primary" label="AI 처리 중..."></b-spinner>
              <p>AI가 이미지를 최적화하고 있습니다...</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 이미지 업로드를 위한 숨겨진 input 추가 -->
    <input ref="imageUploadInput" type="file" accept="image/*" class="d-none" @change="handleImageUpload">
    
    <!-- 전체 화면 미리보기 모달 -->
    <div v-if="fullscreenPreview" class="fullscreen-preview-modal" @click="fullscreenPreview = false">
      <div class="fullscreen-preview-container">
        <img :src="currentImageUrl" alt="이미지 미리보기" class="fullscreen-preview-image">
        <button class="close-fullscreen-btn" @click.stop="fullscreenPreview = false">
          <i class="las la-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ImageEditor from './ImageEditor.vue'
import { mapState, mapActions, mapGetters } from 'vuex'
// import imageService from '../../services/imageService'
import mainImageService from '../../services/works/mainImageService'

export default {
  name: "MainImageEdit",
  components: {
    ImageEditor
  },
  data() {
    return {
      activeTab: 'main',
      selectedImageType: '',
      selectedImageIndex: -1,
      selectedImageUrl: null,
      currentMainImage: null,
      resizeWidth: 800,
      resizeHeight: 800,
      isRemovingText: false,
      editedImageUrl: null,
      imageHistory: {
        original: null,
        processed: null
      },
      processedImages: [],
      optionImages: [],
      editorRequestId: '', // xiangji 에디터에 전달할 요청 ID
      editorImageUrl: '', // xiangji 에디터에 전달할 이미지 URL
      showEditor: false, // 에디터 표시 여부 (작업 완료 시에만 true)
      fullscreenPreview: false, // 전체 화면 미리보기 상태
      editorInitAttempts: 0, // 에디터 초기화 시도 횟수
      lastEditorError: null, // 마지막 에디터 오류 정보
      editorInitTimestamps: [] // 에디터 초기화 시도 시간 기록
    };
  },
  computed: {
    // Vuex 상태 매핑
    ...mapState({
      product: state => state.selectedRocketProduct,
      selectedOption: state => state.selectedOption,
      selectedWorkType: state => state.selectedWorkType,
      processing: state => state.processing,
      progressMessage: state => state.progressMessage
    }),

    // Vuex 게터 매핑
    ...mapGetters([
      'getCurrentOptionImageData',
    ]),

    // 로딩 상태
    loading() {
      return this.processing;
    },

    // 메인 이미지 목록
    mainImages() {
      return this.product?.mainImages || [];
    },

    // 상세 이미지 목록
    detailImages() {
      return this.product?.detailImages || [];
    },

    // 현재 작업 중인 이미지 URL
    currentImageUrl() {
      return this.currentMainImage ||
        (this.selectedOption && this.selectedOption.mainImageStatus && this.selectedOption.mainImageStatus.processedImage) ||
        (this.selectedOption && this.selectedOption.mainImageStatus && this.selectedOption.mainImageStatus.image) ||
        this.selectedImageUrl || '';
    }
  },
  watch: {
    selectedOption: {
      handler() {
        // 선택된 옵션이 변경되면 컴포넌트 다시 초기화
        this.initializeComponent();
      },
      deep: true
    }
  },
  mounted() {
    console.log('MainImageEdit 마운트됨, 선택된 옵션:', this.selectedOption);
    if (this.selectedOption && this.selectedOption.mainImageStatus) {
      console.log('메인 이미지 상태:', this.selectedOption.mainImageStatus);
      console.log('처리된 이미지 URL:', this.selectedOption.mainImageStatus.processedImage);
    }
    // 컴포넌트 마운트 시 초기화
    this.initializeComponent();

    // 키보드 이벤트 리스너 추가
    window.addEventListener('keydown', this.handleKeyDown);
    
    // 마운트 시 기본적으로 에디터 모드 꺼두기 (이미지 먼저 보여주기)
    this.showEditor = false;
  },
  beforeDestroy() {
    // 컴포넌트 제거 시 이벤트 리스너 제거
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    // Vuex 액션 매핑
    ...mapActions([
      'updateOptionImage',
      'processOptionImage'
    ]),
    
    // 편집기 모드 전환
    toggleEditor() {
      // 현재 이미지가 없으면 무시
      if (!this.currentImageUrl) return;
      
      if (this.showEditor) {
        // 편집기를 닫을 때
        this.showEditor = false;
      } else {
        // 편집기를 열 때
        // requestId 확인 - 있으면 바로 에디터 열기
        if (this.editorRequestId) {
          this.showEditor = true;
          
          // 에디터 리셋 (다음 렌더링 사이클에서)
          this.$nextTick(() => {
            if (this.$refs.imageEditor) {
              this.$refs.imageEditor.resetEditor();
            }
          });
        } else {
          // requestId가 없으면 AI 처리 먼저 실행
          this.removeTextFromImage();
        }
      }
    },
    
    /**
     * 에디터 초기화 타임아웃 진단 및 복구
     * - 에디터 상태를 체크하고 타임아웃 원인을 진단
     * - 필요 시 대안 솔루션을 시도
     */
    diagnoseEditorTimeout() {
      // 진단 세션 ID
      const diagId = Date.now().toString(36);
      
      console.log(`[진단 ID: ${diagId}] 에디터 타임아웃 진단 시작:`, {
        timestamp: new Date().toISOString(),
        attempts: this.editorInitAttempts,
        requestId: this.editorRequestId || '없음',
        imageUrl: this.editorImageUrl ? `${this.editorImageUrl.substring(0, 30)}...` : '없음'
      });
      
      // 시도 횟수 증가
      this.editorInitAttempts++;
      
      // 시도 시간 기록
      this.editorInitTimestamps.push({
        timestamp: new Date().toISOString(),
        attemptNumber: this.editorInitAttempts
      });
      
      // 페이지 상태 확인
      console.log(`[진단 ID: ${diagId}] 페이지 상태:`, {
        readyState: document.readyState,
        visibilityState: document.visibilityState,
        isFocused: document.hasFocus(),
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight
      });
      
      // iframe 요소 상태 확인
      try {
        const iframe = document.getElementById('xiangji-image-editor');
        
        if (iframe) {
          console.log(`[진단 ID: ${diagId}] iframe 상태:`, {
            exists: true,
            src: iframe.src,
            width: iframe.offsetWidth,
            height: iframe.offsetHeight,
            visible: iframe.offsetWidth > 0 && iframe.offsetHeight > 0,
            contentWindow: !!iframe.contentWindow,
            contentDocument: !!iframe.contentDocument,
            parentElement: iframe.parentElement ? iframe.parentElement.className : '없음'
          });
          
          // 에디터 컨테이너 스타일 확인
          const container = iframe.parentElement;
          if (container) {
            console.log(`[진단 ID: ${diagId}] 에디터 컨테이너 상태:`, {
              className: container.className,
              style: {
                display: window.getComputedStyle(container).display,
                visibility: window.getComputedStyle(container).visibility,
                position: window.getComputedStyle(container).position,
                width: window.getComputedStyle(container).width,
                height: window.getComputedStyle(container).height,
                zIndex: window.getComputedStyle(container).zIndex
              }
            });
          }
        } else {
          console.log(`[진단 ID: ${diagId}] iframe 요소가 DOM에 존재하지 않음`);
        }
      } catch (err) {
        console.error(`[진단 ID: ${diagId}] iframe 상태 확인 오류:`, err);
      }
      
      // 컴포넌트 참조 확인
      console.log(`[진단 ID: ${diagId}] 컴포넌트 참조:`, {
        imageEditorRef: !!this.$refs.imageEditor,
        editorReady: this.$refs.imageEditor ? this.$refs.imageEditor.isEditorReady() : false,
        editorResult: this.$refs.imageEditor ? !!this.$refs.imageEditor.getEditResult() : false
      });
      
      // 복구 전략 결정
      let recoveryStrategy = 'none';
      
      if (this.editorInitAttempts >= 3) {
        recoveryStrategy = 'disableEditor';
        console.log(`[진단 ID: ${diagId}] 복구 전략: 에디터 비활성화 (시도 한계 초과)`);
      } else if (!this.requestId) {
        recoveryStrategy = 'regenerateRequestId';
        console.log(`[진단 ID: ${diagId}] 복구 전략: RequestId 재생성 필요`);
      } else {
        recoveryStrategy = 'resetEditor';
        console.log(`[진단 ID: ${diagId}] 복구 전략: 에디터 리셋 시도`);
      }
      
      // 복구 전략 실행
      switch (recoveryStrategy) {
        case 'disableEditor':
          // 에디터 비활성화하고 단순 이미지 뷰어로 변경
          this.showEditor = false;
          this.$bvToast.toast('에디터를 로드할 수 없어 기본 모드로 전환합니다', {
            title: '알림',
            variant: 'warning',
            autoHideDelay: 5000,
            toaster: 'b-toaster-bottom-right'
          });
          break;
          
        case 'regenerateRequestId':
          // 새 RequestId를 얻기 위해 텍스트 제거 다시 시도
          this.retryTextRemoval();
          break;
          
        case 'resetEditor':
          // 에디터 컴포넌트 재설정
          if (this.$refs.imageEditor) {
            console.log(`[진단 ID: ${diagId}] 에디터 리셋 시도`);
            setTimeout(() => {
              this.$refs.imageEditor.resetEditor();
            }, 500);
          }
          break;
      }
      
      return {
        diagId,
        recoveryStrategy,
        timestamp: new Date().toISOString(),
        attempts: this.editorInitAttempts
      };
    },

    async initializeComponent() {
      // 기본 상태 초기화
      this.activeTab = 'main';
      this.selectedImageType = '';
      this.selectedImageIndex = -1;
      this.showEditor = false; // 에디터는 기본적으로 표시하지 않음
      this.isRemovingText = false; // 텍스트 제거중 상태 초기화

      // 옵션 이미지 로드
      this.loadOptionImages();

      // 이미지 데이터 초기화 호출
      this.initializeMainImageData();

      // 선택된 옵션과 이미지 데이터 확인
      console.log('초기화 시 선택된 옵션:', this.selectedOption);
      if (this.selectedOption && this.selectedOption.mainImageStatus) {
        console.log('초기화 시 이미지 상태:', this.selectedOption.mainImageStatus);
        console.log('처리된 이미지 URL:', this.selectedOption.mainImageStatus.processedImage);
      }
    },
    // MainImageEdit.vue - initializeMainImageData 메서드 수정
    async initializeMainImageData() {
      console.log("메인 이미지 데이터 초기화 시작");
      console.log("선택된 제품:", this.product);
      console.log("선택된 옵션:", this.selectedOption);

      // 선택된 옵션의 이미지 정보로 컴포넌트 상태 초기화
      if (this.selectedOption && this.selectedOption.mainImageStatus) {
        console.log("옵션 이미지 상태:", this.selectedOption.mainImageStatus);
        let imageToUse = null;
        let requestId = '';

        // 처리된 이미지가 있으면 사용
        if (this.selectedOption.mainImageStatus.processedImage) {
          imageToUse = this.selectedOption.mainImageStatus.processedImage;
          this.currentMainImage = imageToUse;
          this.imageHistory.processed = imageToUse;

          console.log("처리된 이미지 URL:", this.currentMainImage);

          // 이미지 번역 API로 requestId 얻기
          try {
            if (this.selectedOption.mainImageStatus.requestId) {
              // 기존 requestId가 있으면 사용
              requestId = this.selectedOption.mainImageStatus.requestId;
              console.log("기존 requestId 사용:", requestId);
            } else {
              // 없는 경우 빈 문자열 유지 (API 자동 호출 안함)
              console.log("requestId가 없지만 API를 자동으로 호출하지 않습니다");
              // 이후 사용자가 직접 '글자 삭제' 버튼 클릭 시 요청 가능
              requestId = '';
            }
          } catch (error) {
            console.error("이미지 번역 요청 오류:", error);
          }

          // 처리된 이미지 목록에 추가
          if (!this.processedImages.some(img => img.url === imageToUse)) {
            this.processedImages.push({
              url: imageToUse,
              type: 'main',
              originalUrl: this.selectedOption.mainImageStatus.image,
              textRemoved: this.selectedOption.mainImageStatus.textRemoved || false,
              requestId: requestId
            });
          }
          
          // 얻은 requestId 저장 (나중에 편집 버튼 클릭 시 사용)
          this.editorRequestId = requestId;
          
          // 기본적으로 이미지 뷰어 표시 (에디터는 버튼 클릭시에만)
          this.showEditor = false;
        } else {
          console.log("처리된 이미지가 없습니다. 원본 이미지 사용");
          // 원본 이미지 사용
          if (this.selectedOption.mainImageStatus.image) {
            imageToUse = this.selectedOption.mainImageStatus.image;
            this.currentMainImage = imageToUse;
            
            // 자동으로 이미지 번역 API 호출하지 않음
            // 사용자가 '글자 삭제' 버튼을 클릭할 때만 requestImageTranslation 호출
          }
          
          // 작업이 완료되지 않은 이미지는 에디터 표시하지 않음
          this.showEditor = false;
        }

        // 원본 이미지 저장
        if (this.selectedOption.mainImageStatus.image) {
          this.imageHistory.original = this.selectedOption.mainImageStatus.image;
        }

        // 이미지 크기 정보 설정
        if (this.selectedOption.mainImageStatus.dimensions) {
          const dimensions = this.selectedOption.mainImageStatus.dimensions.split('x');
          if (dimensions.length === 2) {
            this.resizeWidth = parseInt(dimensions[0].trim());
            this.resizeHeight = parseInt(dimensions[1].trim());
          }
        }

        // 에디터에 이미지 로드 (에디터를 표시할 때만)
        if (imageToUse) {
          this.editorImageUrl = imageToUse;
          this.editorRequestId = requestId;
          
          if (this.showEditor) {
            console.log('에디터 초기화 시작: requestId=', requestId || '없음');
            
            // $nextTick을 사용하여 DOM이 업데이트된 후 에디터 초기화
            this.$nextTick(() => {
              if (this.$refs.imageEditor) {
                console.log('에디터 컴포넌트 초기화');
                // 간단히 resetEditor 메서드 호출
                this.$refs.imageEditor.resetEditor();
              }
            });
          }
        }

        console.log("메인 이미지 데이터 초기화 완료:", this.currentMainImage);
        console.log("에디터 표시 여부:", this.showEditor);
      } else {
        console.log("초기화할 옵션 이미지 정보가 없습니다");
        this.showEditor = false;
      }
    },

    // 참고: 이 메서드들은 이제 사용되지 않음 (iframe 에디터를 사용하므로)
    // 필요한 경우 이미지 URL을 리턴하는 기능으로 대체
    getImageHtml() {
      return this.currentImageUrl || '';
    },

    // eslint-disable-next-line no-unused-vars
    getImageHtmlFromUrl(imageUrl) {
      // 파라미터는 호환성을 위해 유지하지만 사용하지 않으므로 eslint 경고 비활성화
      return '';
    },

    // 이미지 선택 처리
    async selectImage(type, index, imageUrl) {
      console.log(`========== ${type} 이미지 선택 ==========`);
      console.log("선택된 인덱스:", index);
      console.log("선택된 이미지 URL:", imageUrl);

      if (!imageUrl) return;

      // 선택 상태 업데이트
      this.selectedImageType = type;
      this.selectedImageIndex = index;
      this.selectedImageUrl = imageUrl;
      this.currentMainImage = imageUrl;
      this.imageHistory.original = imageUrl;
      
      // requestId 확인
      let requestId = '';
      
      // 옵션 이미지인 경우 requestId 가져오기 시도
      if (type === 'option' && this.optionImages[index] && this.optionImages[index].requestId) {
        requestId = this.optionImages[index].requestId;
        console.log(`옵션 이미지에서 requestId 찾음: ${requestId}`);
        
        // requestId 저장 (편집기 사용 시 활용)
        if (requestId) {
          this.editorRequestId = requestId;
          this.editorImageUrl = imageUrl;
          
          // 기본적으로 이미지 표시 모드로 설정 (편집 버튼 클릭 시 에디터 표시)
          this.showEditor = false;
          
          // 이미지 데이터 구성 (requestId 포함)
          const imageData = {
            originalUrl: imageUrl,
            processedUrl: imageUrl,
            dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
            textRemoved: true,
            requestId: requestId
          };
          
          // Vuex 액션 호출
          if (this.selectedOption) {
            await this.updateOptionImage({
              registrationId: this.product.registrationId,
              optionNumber: this.selectedOption.optionNumber,
              imageData
            });
          }
          
          return;
        }
      }
      
      // requestId가 없는 경우 (기본 동작)
      this.showEditor = false;
      this.editorRequestId = '';
      this.editorImageUrl = imageUrl;

      // 이미지 데이터 구성 (S3 업로드 없이)
      const imageData = {
        originalUrl: imageUrl,
        processedUrl: imageUrl,
        dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
        textRemoved: false
      };

      // Vuex 액션 호출하여 작업이미지에 추가
      if (this.selectedOption) {
        await this.updateOptionImage({
          registrationId: this.product.registrationId,
          optionNumber: this.selectedOption.optionNumber,
          imageData
        });

        // mainImageService의 updateProductWithCoupangDocPartial 호출
        console.log('=== MainImageEdit 이미지 선택 - coupangDoc 업데이트 시작 ===');
        console.log('imageData:', imageData);

        const updateSuccess = await mainImageService.updateProductWithCoupangDocPartial(
          this.product,
          this.selectedOption,
          imageData
        );

        console.log('coupangDoc 업데이트 결과:', updateSuccess);

        if (!updateSuccess) {
          console.warn('coupangDoc 업데이트 실패');
        }
      }
    },

    // 처리된 이미지 선택
    selectProcessedImage(index, image) {
      console.log("========== 작업된 이미지 선택 ==========");
      console.log("선택된 인덱스:", index);
      console.log("선택된 이미지:", image);

      // 이미지가 유효한지 확인
      if (!image || !image.url) {
        console.error("유효하지 않은 이미지 정보입니다.");
        return;
      }

      this.selectedImageType = 'processed';
      this.selectedImageIndex = index;
      this.selectedImageUrl = image.url;
      this.currentMainImage = image.url;

      // requestId가 있으면 설정, 없으면 이미지 URL만 설정
      this.editorRequestId = image.requestId || '';
      this.editorImageUrl = image.url;
      
      // 처리된 이미지도 먼저 이미지 뷰어로 표시 (편집 버튼 클릭 시 에디터 표시)
      this.showEditor = false;

      // mainImageService의 updateProductWithCoupangDocPartial 사용
      const imageData = {
        originalUrl: image.originalUrl || image.url,
        processedUrl: image.url,
        dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
        filesize: this.estimateFileSize(image.url), // 못 재면 '알 수 없음'(지어낸 기본 파일 크기 0)
        textRemoved: image.textRemoved || false,
        requestId: image.requestId || ''
      };

      // Vuex 액션 호출하여 작업이미지에 추가
      this.updateOptionImage({
        registrationId: this.product.registrationId,
        optionNumber: this.selectedOption.optionNumber,
        imageData
      }).then(() => {
        // mainImageService의 updateProductWithCoupangDocPartial 호출
        return mainImageService.updateProductWithCoupangDocPartial(
          this.product,
          this.selectedOption,
          imageData
        );
      }).then((updateSuccess) => {
        if (!updateSuccess) {
          console.warn('coupangDoc 업데이트 실패');
        }
        this.$bvToast.toast('이미지가 적용되었습니다.', {
          title: '성공',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      }).catch(error => {
        console.error("이미지 저장 중 오류 발생:", error);
        this.$bvToast.toast('이미지 저장 중 오류가 발생했습니다', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      });
    },

    // AI 이미지 처리
    async removeTextFromImage() {
      console.log("========== AI 이미지 처리 시작 ==========");
      console.log("현재 선택된 옵션:", this.selectedOption);
      console.log("현재 이미지 URL:", this.currentMainImage);

      // 현재 이미지가 없으면 중단
      if (!this.currentMainImage) {
        this.$bvToast.toast('이미지를 먼저 선택해주세요', {
          title: '알림',
          variant: 'warning',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      // 로딩 시작
      this.isRemovingText = true;

      try {
        // Vuex 액션으로 텍스트 제거 처리 요청
        const optionDummy = {
          optionNumber: this.selectedOption.optionNumber,
          mainImageStatus: {
            image: this.currentMainImage
          }
        };
        console.log("processOption 호출 전 매개변수:", optionDummy);
        const result = await mainImageService.processOption(optionDummy, this.product.registrationId);
        console.log("processOption 결과:", result);

        if (result && result.processedUrl) {
          console.log("결과에 processedUrl 있음:", result.processedUrl);
          console.log("결과에 requestId 있음:", result.requestId || '없음');
          
          // 현재 이미지 업데이트
          this.currentMainImage = result.processedUrl;

          // 에디터 속성 업데이트
          this.editorImageUrl = result.processedUrl;
          this.editorRequestId = result.requestId || '';
          
          console.log("에디터 속성 업데이트 완료:", {
            url: this.editorImageUrl,
            requestId: this.editorRequestId
          });
          
          // 이미지 작업이 완료되면 편집기 표시
          this.showEditor = true;
          
          // 에디터 리셋 (에디터가 표시될 때만)
          console.log('AI 처리 후 에디터 리셋 준비');
          
          this.$nextTick(() => {
            if (this.$refs.imageEditor) {
              console.log('에디터 리셋 호출');
              this.$refs.imageEditor.resetEditor();
            }
          });

          // 처리된 이미지 목록에 추가
          if (!this.processedImages.some(img => img.url === result.processedUrl)) {
            this.processedImages.push({
              url: result.processedUrl,
              type: 'main',
              originalUrl: result.originalUrl || this.imageHistory.original,
              textRemoved: result.textRemoved || false,
              requestId: result.requestId || ''
            });
          }

          // 작업 완료 후 processed 탭으로 변경
          this.activeTab = 'processed';

          // 처리 결과 메시지
          this.$bvToast.toast(
            result.textRemoved ? 'AI가 이미지를 성공적으로 처리했습니다.' : 'AI 처리가 필요하지 않거나 처리할 수 없는 이미지입니다.',
            {
              title: '처리 완료',
              variant: 'success',
              solid: true,
              toaster: 'b-toaster-bottom-right'
            }
          );
        } else {
          throw new Error('이미지 처리 결과가 없습니다.');
        }

        console.log("========== AI 이미지 처리 완료 ==========");
      } catch (error) {
        console.error('AI 처리 오류:', error);
        this.$bvToast.toast(`AI 이미지 처리 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right'
        });
      } finally {
        // 로딩 종료
        this.isRemovingText = false;
      }
    },

    // 이미지 크기 변경 적용
    applyResize() {
      // 현재 메인 이미지 가져오기
      const imageToResize = this.currentMainImage;
      if (!imageToResize) return;

      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = this.resizeWidth;
        canvas.height = this.resizeHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, this.resizeWidth, this.resizeHeight);

        const resizedImageUrl = canvas.toDataURL('image/jpeg', 0.9);

        // 현재 메인 이미지 업데이트
        this.currentMainImage = resizedImageUrl;
        
        // 에디터 이미지 URL 업데이트
        this.editorImageUrl = resizedImageUrl;
        
        // 에디터 리셋 (새 이미지로 다시 로드)
        if (this.$refs.imageEditor) {
          this.$nextTick(() => {
            this.$refs.imageEditor.resetEditor();
          });
        }

        // Vuex 액션 호출하여 이미지 업데이트
        if (this.selectedOption) {
          this.updateOptionImage({
            registrationId: this.product.registrationId,
            optionNumber: this.selectedOption.optionNumber,
            imageData: {
              originalUrl: this.imageHistory.original || resizedImageUrl,
              processedUrl: resizedImageUrl,
              dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
              filesize: this.estimateFileSize(resizedImageUrl)
            }
          });
        }
      };

      img.src = imageToResize;
    },

    // 옵션 이미지 로드
    loadOptionImages() {
      this.optionImages = [];
      this.processedImages = []; // 작업된 이미지 목록 초기화

      // 중복 체크를 위한 URL 세트
      const processedImageUrls = new Set();
      const optionImageUrls = new Set();

      // 상품에 옵션이 있는 경우
      if (this.product && this.product.options && this.product.options.length > 0) {
        this.product.options.forEach((option, index) => {
          // 유효성 검사 추가
          if (!option) return;

          // 옵션1 이미지
          if (option.option1 && option.option1.imageUrl) {
            const imageUrl = option.option1.imageUrl;
            if (!optionImageUrls.has(imageUrl)) {
              optionImageUrls.add(imageUrl);
              this.optionImages.push({
                url: imageUrl,
                label: `옵션1-${option.option1.nameTrans || option.option1.name}`,
                optionNumber: option.optionNumber,
                optionIndex: index
              });
            }
          }

          // 옵션2 이미지
          if (option.option2 && option.option2.imageUrl) {
            const imageUrl = option.option2.imageUrl;
            if (!optionImageUrls.has(imageUrl)) {
              optionImageUrls.add(imageUrl);
              this.optionImages.push({
                url: imageUrl,
                label: `옵션2-${option.option2.nameTrans || option.option2.name}`,
                optionNumber: option.optionNumber,
                optionIndex: index
              });
            }
          }

          // 옵션 메인 이미지 (모든 처리된 이미지를 processedImages에 추가)
          if (option.mainImageStatus && option.mainImageStatus.processedImage) {
            const processedImageUrl = option.mainImageStatus.processedImage;
            // requestId 가져오기
            const requestId = option.mainImageStatus.requestId || '';

            // 이미 추가된 URL인지 확인
            if (!processedImageUrls.has(processedImageUrl)) {
              // 중복이 아닌 경우만 추가
              processedImageUrls.add(processedImageUrl);

              // 처리된 이미지가 있는 경우 모두 작업이미지로 분류
              const processedImg = {
                url: processedImageUrl,
                label: `옵션${index + 1}-처리됨`,
                optionNumber: option.optionNumber,
                optionIndex: index,
                isProcessed: true,
                textRemoved: option.mainImageStatus.textRemoved || false,
                originalUrl: option.mainImageStatus.image,
                requestId: requestId  // requestId 추가
              };

              // 작업된 이미지 목록에 추가
              this.processedImages.push(processedImg);
            }

            // 옵션 이미지에도 추가 (URL 중복 체크)
            if (!optionImageUrls.has(processedImageUrl)) {
              optionImageUrls.add(processedImageUrl);
              this.optionImages.push({
                url: processedImageUrl,
                label: option.mainImageStatus.textRemoved ? `옵션${index + 1}-텍스트제거` : `옵션${index + 1}-처리됨`,
                optionNumber: option.optionNumber,
                optionIndex: index,
                isProcessed: true,
                requestId: requestId  // requestId 추가
              });
            }
          } else if (option.mainImageStatus && option.mainImageStatus.image) {
            const imageUrl = option.mainImageStatus.image;
            if (!optionImageUrls.has(imageUrl)) {
              optionImageUrls.add(imageUrl);
              this.optionImages.push({
                url: imageUrl,
                label: `옵션${index + 1}-원본`,
                optionNumber: option.optionNumber,
                optionIndex: index
              });
            }
          }
        });
      }

      // 현재 선택된 옵션의 처리된 이미지가 있는지 확인하고 표시
      if (this.selectedOption && this.selectedOption.mainImageStatus) {
        // 처리된 이미지가 있는 경우
        if (this.selectedOption.mainImageStatus.processedImage) {
          const processedImageUrl = this.selectedOption.mainImageStatus.processedImage;

          // 이미 processedImages에 있는지 확인
          if (!processedImageUrls.has(processedImageUrl)) {
            // 목록에 없으면 추가
            processedImageUrls.add(processedImageUrl);
            this.processedImages.push({
              url: processedImageUrl,
              label: `현재 옵션`,
              optionNumber: this.selectedOption.optionNumber,
              textRemoved: this.selectedOption.mainImageStatus.textRemoved || false,
              originalUrl: this.selectedOption.mainImageStatus.image
            });
          }
        }
        // loadOptionImages 메서드 이어서...
        // 원본 이미지가 있는 경우 (processedImage가 없는 경우에만 추가)
        else if (this.selectedOption.mainImageStatus.image && !this.selectedOption.mainImageStatus.processedImage) {
          const imageUrl = this.selectedOption.mainImageStatus.image;
          if (!optionImageUrls.has(imageUrl)) {
            optionImageUrls.add(imageUrl);
            this.optionImages.push({
              url: imageUrl,
              label: `현재 옵션-원본`,
              optionNumber: this.selectedOption.optionNumber
            });
          }
        }
      }

      // 기본적으로 처리된 이미지 탭을 먼저 보여주기
      if (this.processedImages.length > 0) {
        this.activeTab = 'processed';
      } else if (this.optionImages.length > 0) {
        this.activeTab = 'option';
      } else if (this.mainImages.length > 0) {
        this.activeTab = 'main';
      }
    },

    // 외부 에디터에서 편집된 이미지 이벤트 처리
    async handleEditorImageEdited(editorResult) {
      // 결과 식별 ID
      const resultId = Date.now().toString(36) + Math.random().toString(36).substring(2);
      
      console.log(`[결과 ID: ${resultId}] 에디터 이미지 편집 결과:`, {
        timestamp: new Date().toISOString(),
        hasImageUrl: !!editorResult?.imageUrl,
        hasRequestId: !!editorResult?.requestId,
        msgId: editorResult?.msgId || '없음',
        requestId: this.editorRequestId || '없음'
      });
      
      // 에디터 초기화 성공 기록 (타임아웃 분석에 중요)
      this.editorInitAttempts = 0; // 초기화 시도 카운터 리셋
      this.editorInitTimestamps.push({
        timestamp: new Date().toISOString(),
        success: true,
        resultId: resultId,
        msgId: editorResult?.msgId
      });
      
      if (!editorResult || !editorResult.imageUrl) {
        console.error(`[결과 ID: ${resultId}] 편집된 이미지 URL이 없습니다:`, editorResult);
        
        // 기존 이미지 URL이 있으면 사용
        if (this.currentMainImage) {
          console.log(`[결과 ID: ${resultId}] 기존 이미지 URL 사용:`, 
            this.currentMainImage.substring(0, 30) + '...');
            
          // 결과 객체 생성
          editorResult = {
            imageUrl: this.currentMainImage,
            requestId: this.editorRequestId || '',
            url: this.currentMainImage,
            result: { url: this.currentMainImage }
          };
        } else {
          this.$bvToast.toast('편집된 이미지를 가져오지 못했습니다', {
            title: '오류',
            variant: 'danger',
            autoHideDelay: 3000,
            toaster: 'b-toaster-bottom-right'
          });
          return;
        }
      }
      
      try {
        // 로딩 상태 시작
        this.isRemovingText = true;
        
        // 이미지 URL 설정 (이미 호스팅된 URL)
        this.editedImageUrl = editorResult.imageUrl;
        this.currentMainImage = editorResult.imageUrl;
        
        // requestId 저장 및 로깅
        if (editorResult.requestId) {
          console.log(`[결과 ID: ${resultId}] requestId 업데이트:`, {
            이전: this.editorRequestId || '없음',
            신규: editorResult.requestId
          });
          this.editorRequestId = editorResult.requestId;
        } else {
          console.log(`[결과 ID: ${resultId}] 응답에 requestId 없음, 기존 ID 유지:`, this.editorRequestId || '없음');
        }
        
        // 처리된 이미지 업데이트 (S3 업로드 없이 직접 호스팅된 URL 사용)
        const updateResult = await this.updateProcessedImage(editorResult.imageUrl);
        
        console.log(`[결과 ID: ${resultId}] 이미지 업데이트 완료:`, {
          url: editorResult.imageUrl.substring(0, 30) + '...',
          success: true,
          timestamp: new Date().toISOString(),
          processedImagesCount: this.processedImages.length,
          updateResult: updateResult
        });
        
        // 저장 후에는 이미지 뷰어 모드로 전환 (에디터 닫기)
        this.showEditor = false;
        
        // 다음 렌더링 사이클에서 에디터 참조 확인
        this.$nextTick(() => {
          if (this.$refs.imageEditor) {
            // 에디터 상태 검사
            console.log(`[결과 ID: ${resultId}] 에디터 상태 확인:`, {
              exists: !!this.$refs.imageEditor,
              requestId: this.editorRequestId || '없음'
            });
          } else {
            console.warn(`[결과 ID: ${resultId}] 에디터 참조 없음`);
          }
        });
        
        // 성공 메시지 표시
        this.$bvToast.toast('이미지가 저장되었습니다', {
          title: '완료',
          variant: 'success',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
      } catch (error) {
        console.error(`[결과 ID: ${resultId}] 편집된 이미지 처리 오류:`, {
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        });
        
        this.$bvToast.toast(`이미지 저장 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 3000,
          toaster: 'b-toaster-bottom-right'
        });
      } finally {
        // 로딩 상태 종료
        this.isRemovingText = false;
      }
    },

    // 이미지 편집기에 변경사항 저장 요청
    async applyEditorChanges() {
      // 저장 요청 ID 생성
      const saveRequestId = Date.now().toString(36);
      console.log(`[저장 ID: ${saveRequestId}] 에디터 변경사항 저장 시작`);
      
      try {
        // 편집기가 있는지 확인
        if (!this.$refs.imageEditor) {
          throw new Error('이미지 편집기를 찾을 수 없습니다');
        }

        // RequestId 확인 및 저장
        console.log(`[저장 ID: ${saveRequestId}] 현재 RequestId:`, this.editorRequestId || '없음');
        
        // 로딩 상태 시작
        this.isRemovingText = true;
        
        // 사용자에게 알림
        this.$bvToast.toast('이미지를 저장 중입니다...', {
          title: '알림',
          variant: 'info',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
        
        // 만약 requestId가 없는 경우 현재 상태 저장
        if (!this.editorRequestId && this.currentImageUrl) {
          console.log(`[저장 ID: ${saveRequestId}] RequestId 없음, 현재 이미지 URL 저장`);
          
          // 현재 이미지로 저장 처리 (API 호출 없이)
          setTimeout(() => {
            this.handleEditorImageEdited({
              requestId: '',
              imageUrl: this.currentImageUrl,
              result: { url: this.currentImageUrl },
              url: this.currentImageUrl
            });
            this.isRemovingText = false;
          }, 1000);
          
          return;
        }
        
        // 외부 에디터에 저장 요청
        console.log(`[저장 ID: ${saveRequestId}] 에디터 saveImage() 호출`);
        this.$refs.imageEditor.saveImage();
        
        // 안전장치: 저장이 완료되지 않을 경우 10초 후 로딩 상태 해제
        setTimeout(() => {
          if (this.isRemovingText) {
            console.log(`[저장 ID: ${saveRequestId}] 저장 타임아웃`);
            this.isRemovingText = false;
          }
        }, 10000);
        
        // 참고: 실제 저장 처리는 handleEditorImageEdited 메서드에서 수행됨
        // 이 메서드는 에디터의 saveImage() 메서드 호출 후 image-edited 이벤트가 
        // 발생했을 때 실행됨
        
      } catch (error) {
        console.error(`[저장 ID: ${saveRequestId}] 에디터 변경사항 적용 중 오류:`, error);
        this.$bvToast.toast(`이미지 저장 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          autoHideDelay: 3000,
          toaster: 'b-toaster-bottom-right'
        });
        // 로딩 상태 종료
        this.isRemovingText = false;
      }
    },
    /**
     * 처리된 이미지 업데이트
     * @param {string} imageUrl - 이미지 URL
     * @returns {Promise<Object>} - 업데이트 결과
     */
    updateProcessedImage(imageUrl) {
      // 프로세스 식별자
      const processId = Date.now().toString(36) + Math.random().toString(36).substring(2);
      
      return new Promise((resolve, reject) => {
        try {
          console.log(`[프로세스 ID: ${processId}] 이미지 업데이트 시작:`, {
            imageType: imageUrl.startsWith('http') ? 'HTTP URL' : 'Data URL',
            imageLength: imageUrl.length,
            timestamp: new Date().toISOString()
          });
          
          // 편집된 이미지에 대한 requestId 저장
          const requestId = this.editorRequestId || '';
          
          // 이미 S3 URL인 경우 (http로 시작하면)
          if (imageUrl.startsWith('http')) {
            const imageData = {
              originalUrl: this.imageHistory.original || imageUrl,
              processedUrl: imageUrl,
              dimensions: `${this.resizeWidth}x${this.resizeHeight}`, // 기본 크기 사용
              filesize: '알 수 없음', // 파일 크기는 알 수 없음
              textRemoved: true,
              requestId: requestId // requestId 추가
            };
            
            console.log(`[프로세스 ID: ${processId}] HTTP URL 이미지 처리:`, {
              dimensions: imageData.dimensions,
              requestId: requestId || '없음'
            });
            
            // Vuex 액션 호출하여 작업이미지에 추가
            if (this.selectedOption) {
              this.updateOptionImage({
                registrationId: this.product.registrationId,
                optionNumber: this.selectedOption.optionNumber,
                imageData
              }).then(() => {
                // mainImageService의 updateProductWithCoupangDocPartial 호출
                return mainImageService.updateProductWithCoupangDocPartial(
                  this.product,
                  this.selectedOption,
                  imageData
                );
              }).then((updateSuccess) => {
                if (!updateSuccess) {
                  console.warn(`[프로세스 ID: ${processId}] coupangDoc 업데이트 실패`);
                }
                // 처리된 이미지 목록에 추가
                const isNewImage = !this.processedImages.some(img => img.url === imageUrl);
                
                if (isNewImage) {
                  this.processedImages.push({
                    url: imageUrl,
                    type: 'main',
                    originalUrl: this.imageHistory.original,
                    textRemoved: true,
                    requestId: requestId, // requestId 추가
                    processId: processId
                  });
                  
                  console.log(`[프로세스 ID: ${processId}] 새 이미지 추가됨:`, {
                    totalImages: this.processedImages.length
                  });
                } else {
                  console.log(`[프로세스 ID: ${processId}] 기존 이미지 업데이트됨`);
                }
                
                // 탭 변경
                this.activeTab = 'processed';
                
                // 성공 응답
                resolve({
                  success: true,
                  type: 'http',
                  processId: processId,
                  isNewImage: isNewImage,
                  timestamp: new Date().toISOString()
                });
              }).catch(error => {
                console.error(`[프로세스 ID: ${processId}] Vuex 액션 오류:`, error);
                reject(error);
              });
            } else {
              console.warn(`[프로세스 ID: ${processId}] selectedOption이 없음`);
              resolve({
                success: false,
                reason: 'no-selected-option',
                processId: processId,
                timestamp: new Date().toISOString()
              });
            }
            return;
          }
          
          // Data URL 처리 (Base64 이미지)
          console.log(`[프로세스 ID: ${processId}] Data URL 이미지 처리 시작`);
          
          const img = new Image();
          
          img.onload = () => {
            const imageData = {
              originalUrl: this.imageHistory.original || imageUrl,
              processedUrl: imageUrl,
              dimensions: `${img.naturalWidth}x${img.naturalHeight}`,
              filesize: this.estimateFileSize(imageUrl),
              textRemoved: true,
              requestId: requestId // requestId 추가
            };
            
            console.log(`[프로세스 ID: ${processId}] 이미지 로드 완료:`, {
              dimensions: imageData.dimensions,
              filesize: imageData.filesize,
              requestId: requestId || '없음'
            });
            
            // Vuex 액션 호출하여 작업이미지에 추가
            if (this.selectedOption) {
              this.updateOptionImage({
                registrationId: this.product.registrationId,
                optionNumber: this.selectedOption.optionNumber,
                imageData
              }).then(() => {
                // mainImageService의 updateProductWithCoupangDocPartial 호출
                return mainImageService.updateProductWithCoupangDocPartial(
                  this.product,
                  this.selectedOption,
                  imageData
                );
              }).then((updateSuccess) => {
                if (!updateSuccess) {
                  console.warn(`[프로세스 ID: ${processId}] coupangDoc 업데이트 실패`);
                }
                // 처리된 이미지 목록에 추가
                const isNewImage = !this.processedImages.some(img => img.url === imageUrl);
                
                if (isNewImage) {
                  this.processedImages.push({
                    url: imageUrl,
                    type: 'main',
                    originalUrl: this.imageHistory.original,
                    textRemoved: true,
                    requestId: requestId, // requestId 추가
                    processId: processId
                  });
                  
                  console.log(`[프로세스 ID: ${processId}] 새 Data URL 이미지 추가됨:`, {
                    totalImages: this.processedImages.length
                  });
                } else {
                  console.log(`[프로세스 ID: ${processId}] 기존 Data URL 이미지 업데이트됨`);
                }
                
                // 탭 변경
                this.activeTab = 'processed';
                
                // 성공 응답
                resolve({
                  success: true,
                  type: 'dataUrl',
                  dimensions: imageData.dimensions,
                  filesize: imageData.filesize,
                  processId: processId,
                  isNewImage: isNewImage,
                  timestamp: new Date().toISOString()
                });
              }).catch(error => {
                console.error(`[프로세스 ID: ${processId}] Data URL Vuex 액션 오류:`, error);
                reject(error);
              });
            } else {
              console.warn(`[프로세스 ID: ${processId}] Data URL selectedOption이 없음`);
              resolve({
                success: false,
                reason: 'no-selected-option',
                processId: processId,
                timestamp: new Date().toISOString()
              });
            }
          };
          
          img.onerror = (error) => {
            console.error(`[프로세스 ID: ${processId}] 이미지 로드 오류:`, error);
            reject(new Error('이미지 로드 실패'));
          };
          
          // 이미지 로드 시작
          img.src = imageUrl;
          
        } catch (error) {
          console.error(`[프로세스 ID: ${processId}] updateProcessedImage 예외:`, error);
          reject(error);
        }
      });
    },

    // 이미지 파일 크기 추정
    estimateFileSize(dataUrl) {
      // 대략적인 파일 크기 계산 (Base64 문자열 길이 * 0.75)
      if (dataUrl && dataUrl.startsWith('data:')) {
        const base64Length = dataUrl.split(',')[1].length;
        const fileSizeBytes = Math.round((base64Length * 0.75));

        if (fileSizeBytes < 1024) {
          return `${fileSizeBytes} B`;
        } else if (fileSizeBytes < 1024 * 1024) {
          return `${Math.round(fileSizeBytes / 1024)} KB`;
        } else {
          return `${(fileSizeBytes / (1024 * 1024)).toFixed(1)} MB`;
        }
      }

      return '알 수 없음';
    },

    // 에디터 초기화
    resetEditor() {
      if (this.$refs.imageEditor) {
        // 에디터 초기화 메서드 호출
        this.$refs.imageEditor.resetEditor();
        this.editedImageUrl = null;
        
        // 필요한 경우 원본 이미지로 돌아감
        const originalImage = this.imageHistory.original;
        if (originalImage && originalImage !== this.editorImageUrl) {
          this.editorImageUrl = originalImage;
          // 에디터는 기존 requestId를 유지 - 여기서 자동 번역을 시도하지 않음
          
          // 에디터 재설정
          this.$nextTick(() => {
            if (this.$refs.imageEditor) {
              this.$refs.imageEditor.resetEditor();
            }
          });
        }
      }
    },

    // 이미지 업로드 관련 메서드
    showImageUpload() {
      this.$refs.imageUploadInput.click();
    },

    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageUrl = e.target.result;

        // 선택 상태 초기화
        this.selectedImageIndex = -1;
        this.selectedImageUrl = imageUrl;
        this.currentMainImage = imageUrl;
        this.imageHistory.original = imageUrl;

        // 에디터 업데이트
        if (this.$refs.imageEditor && this.$refs.imageEditor.editor) {
          const newHtml = this.getImageHtmlFromUrl(imageUrl);
          this.$refs.imageEditor.editor.setComponents(newHtml);
        }

        // 이미지를 S3에 업로드
        if (this.selectedOption) {
          try {
            // base64 데이터 추출
            const base64Data = imageUrl;

            // 파일 이름 생성 (mainImageService 패턴에 맞춤)
            const supplierCode = this.$store.state.user.suppliercode || this.$store.state.user.companyid || 'default';
            const fileName = `${supplierCode}_main_manual_${Date.now()}.jpg`;

            // S3 업로드 (서비스 활용)
            const uploadedUrl = await mainImageService.uploadImage({
              imageData: base64Data,
              filename: fileName,
              type: 'main'
            });

            if (uploadedUrl) {
              // mainImageService의 updateProductWithCoupangDocPartial 방식으로 업데이트
              const imageData = {
                originalUrl: uploadedUrl,
                processedUrl: uploadedUrl,
                dimensions: `${this.resizeWidth}x${this.resizeHeight}`,
                filesize: this.estimateFileSize(base64Data),
                textRemoved: false,
                requestId: `manual_${Date.now()}`
              };

              // Vuex 액션 호출하여 작업이미지에 추가
              await this.updateOptionImage({
                registrationId: this.product.registrationId,
                optionNumber: this.selectedOption.optionNumber,
                imageData
              });

              // mainImageService의 updateProductWithCoupangDocPartial 호출
              console.log('=== MainImageEdit 업로드 - coupangDoc 업데이트 시작 ===');
              console.log('product:', this.product);
              console.log('selectedOption:', this.selectedOption);
              console.log('imageData:', imageData);

              const updateSuccess = await mainImageService.updateProductWithCoupangDocPartial(
                this.product,
                this.selectedOption,
                imageData
              );

              console.log('coupangDoc 업데이트 결과:', updateSuccess);

              if (updateSuccess) {
                this.$bvToast.toast('이미지가 업로드되고 상품 정보가 업데이트되었습니다', {
                  title: '성공',
                  variant: 'success',
                  solid: true,
                  toaster: 'b-toaster-bottom-right'
                });
              } else {
                this.$bvToast.toast('이미지는 업로드되었지만 상품 정보 업데이트에 실패했습니다', {
                  title: '경고',
                  variant: 'warning',
                  solid: true,
                  toaster: 'b-toaster-bottom-right'
                });
              }
            }
          } catch (error) {
            console.error('이미지 업로드 중 오류:', error);
            this.$bvToast.toast('이미지 업로드 중 오류가 발생했습니다', {
              title: '오류',
              variant: 'danger',
              solid: true,
              toaster: 'b-toaster-bottom-right'
            });
          }
        }
      };
      reader.readAsDataURL(file);
    },

    // 에디터 오류 처리
    handleEditorError(errorMessage) {
      // 오류 세션 ID 생성
      const errorId = Date.now().toString(36) + Math.random().toString(36).substring(2);
      
      console.error(`[오류 ID: ${errorId}] 이미지 편집기 로드 오류:`, {
        message: errorMessage,
        timestamp: new Date().toISOString(),
        requestId: this.editorRequestId || '없음',
        imageUrl: this.editorImageUrl ? `${this.editorImageUrl.substring(0, 30)}...` : '없음',
        showEditor: this.showEditor,
        isRemovingText: this.isRemovingText
      });
      
      // DOM 상태 확인
      try {
        const iframe = document.getElementById('xiangji-image-editor');
        
        if (iframe) {
          console.log(`[오류 ID: ${errorId}] iframe 상태:`, {
            exists: true,
            src: iframe.src,
            width: iframe.offsetWidth,
            height: iframe.offsetHeight,
            visible: iframe.offsetWidth > 0 && iframe.offsetHeight > 0,
            contentWindow: !!iframe.contentWindow,
            parent: iframe.parentElement ? iframe.parentElement.className : '없음'
          });
        } else {
          console.log(`[오류 ID: ${errorId}] iframe 요소가 DOM에 존재하지 않음`);
        }
        
        // 컴포넌트 참조 확인
        console.log(`[오류 ID: ${errorId}] 컴포넌트 참조:`, {
          imageEditorRef: !!this.$refs.imageEditor,
          editorMethods: this.$refs.imageEditor ? Object.keys(this.$refs.imageEditor) : []
        });
      } catch (err) {
        console.error(`[오류 ID: ${errorId}] DOM 상태 확인 중 오류:`, err);
      }
      
      // 디버깅 정보 저장
      this.lastEditorError = {
        id: errorId,
        message: errorMessage,
        timestamp: new Date().toISOString()
      };
      
      // 오류 유형 확인
      const isTimeoutError = errorMessage.includes('시간 초과') || 
                            errorMessage.includes('timeout') || 
                            errorMessage.includes('timed out');
      
      // 타임아웃 오류인 경우 진단 및 복구 시도
      if (isTimeoutError) {
        console.log(`[오류 ID: ${errorId}] 타임아웃 오류 감지, 진단 및 복구 시작`);
        const diagResult = this.diagnoseEditorTimeout();
        
        // 복구 전략에 따른 사용자 메시지
        if (diagResult.recoveryStrategy === 'disableEditor') {
          this.$bvToast.toast('에디터 로드 시간이 초과되어 기본 모드로 전환합니다', {
            title: '알림',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 5000
          });
        } else if (diagResult.recoveryStrategy === 'regenerateRequestId') {
          this.$bvToast.toast('에디터 로드를 위한 정보가 부족합니다. 다시 시도합니다.', {
            title: '알림',
            variant: 'info',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 3000
          });
        } else {
          this.$bvToast.toast(`이미지 편집기 초기화 중 문제가 발생했습니다. 다시 시도합니다.`, {
            title: '알림',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 3000
          });
        }
      } else {
        // 일반 오류 처리 (타임아웃 외 다른 오류)
        this.$bvToast.toast(`이미지 편집기 로드 실패: ${errorMessage}`, {
          title: '오류',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 5000
        });
        
        // 일반 오류는 에디터 비활성화
        this.showEditor = false;
      }
    },

    // 1688에서 이미지 불러오기
    fetchImagesFrom1688() {
      // Vuex 액션 호출 또는 서비스 메서드 호출
      mainImageService.fetchImagesFrom1688(this.product.구매링크)
        .then(images => {
          if (images && images.length > 0) {
            // 이미지 처리 로직
            this.$bvToast.toast('1688에서 이미지를 불러왔습니다', {
              title: '성공',
              variant: 'success',
              solid: true,
              toaster: 'b-toaster-bottom-right'
            });
          } else {
            this.$bvToast.toast('가져올 이미지가 없습니다', {
              title: '알림',
              variant: 'info',
              solid: true,
              toaster: 'b-toaster-bottom-right'
            });
          }
        })
        .catch(error => {
          console.error('이미지 불러오기 오류:', error);
          this.$bvToast.toast('이미지를 불러오는 중 오류가 발생했습니다', {
            title: '오류',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        });
    },
    // 전체 화면 미리보기 표시
    showFullscreenPreview() {
      if (this.currentImageUrl) {
        this.fullscreenPreview = true;
      }
    },
    
    // 텍스트 제거 다시 시도
    retryTextRemoval() {
      // 재시도 ID 생성
      const retryId = Date.now().toString(36) + Math.random().toString(36).substring(2);
      
      console.log(`[재시도 ID: ${retryId}] 텍스트 제거 재시도:`, {
        timestamp: new Date().toISOString(),
        currentImageUrl: this.currentImageUrl ? '있음' : '없음',
        editorImageUrl: this.editorImageUrl ? '있음' : '없음',
        requestId: this.editorRequestId || '없음',
        lastError: this.lastEditorError 
          ? `${this.lastEditorError.timestamp} - ${this.lastEditorError.message}` 
          : '없음'
      });
      
      // 프로세스 추적
      const startRetry = () => {
        console.log(`[재시도 ID: ${retryId}] removeTextFromImage 함수 호출 시작`);
        this.removeTextFromImage();
        
        // 추적 데이터 저장
        this.lastRetryAttempt = {
          id: retryId,
          timestamp: new Date().toISOString(),
          imageUrl: this.currentImageUrl ? 
            this.currentImageUrl.substring(0, 30) + '...' : '없음'
        };
      };
      
      // 현재 이미지로 텍스트 제거 다시 시도
      if (this.currentImageUrl) {
        // 에디터 상태 확인 로깅
        console.log(`[재시도 ID: ${retryId}] 에디터 상태:`, {
          showEditor: this.showEditor,
          editorRef: !!this.$refs.imageEditor,
          editorInitialized: this.$refs.imageEditor 
            ? this.$refs.imageEditor.isEditorReady() 
            : false
        });
        
        // 즉시 재시도
        startRetry();
      } else {
        console.warn(`[재시도 ID: ${retryId}] 이미지 없음, 재시도 불가`);
        
        this.$bvToast.toast('이미지를 먼저 선택해주세요', {
          title: '알림',
          variant: 'warning',
          autoHideDelay: 2000,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },
    
    // 키보드 이벤트 처리
    handleKeyDown(e) {
      if (e.key === 'Escape' && this.fullscreenPreview) {
        this.fullscreenPreview = false;
      }
    },
  }
};
</script>

<style scoped>
.main-image-edit {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 레이아웃 컨테이너 */
.image-editor-layout {
  display: flex;
  height: 100%;
  background-color: #f9f9f9;
}

/* 좌측 이미지 선택 패널 */
.image-selector-panel {
  width: 280px;
  background-color: #fff;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 15px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

/* 이미지 탭 스타일 */
.image-tabs {
  border-bottom: 1px solid #eee;
}

.image-tabs .nav-tabs {
  border-bottom: none;
}

.image-tabs .nav-link {
  font-size: 12px;
  padding: 5px 10px;
  color: #777;
}

.image-tabs .nav-link.active {
  color: #3498db;
  font-weight: 600;
}

.image-gallery {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.image-item {
  position: relative;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  aspect-ratio: 1/1;
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.image-item.selected {
  border-color: #3498db;
}

.image-overlay {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background: #3498db;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-item.selected .image-overlay {
  opacity: 1;
}

/* 이미지 라벨 스타일 추가 */
.image-label {
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  padding: 3px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty-gallery {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #aaa;
  text-align: center;
  padding: 20px;
}

.image-options {
  padding: 15px;
  border-top: 1px solid #eee;
}

.options-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #555;
}

/* 이미지 편집 버튼 영역 스타일 */
.image-actions-panel {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #eee;
}

/* 우측 에디터 패널 */
.editor-main-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 600px; /* 높이 증가 */
  position: relative;
  overflow: hidden;
  width: 100%; /* 너비 명시 */
}

/* 에디터 헤더 제거로 인한 스타일 제거 */

.editor-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5; /* 배경색 변경 - 연한 회색으로 변경하여 이미지 구분 */
  padding: 0;
  min-height: 600px;
  flex: 1;
  position: relative;
  overflow: hidden;
  border-top: 1px solid #eee;
}

.image-container {
  position: relative;
  width: min(100%, 500px);
  height: min(100%, 500px);
  aspect-ratio: 1/1;
  background-color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 0 auto;
}

.image-container-full {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100% !important;
  height: 100% !important;
  background-color: #fff;
  box-shadow: none;
  overflow: hidden;
  z-index: 1;
  margin: 0;
  padding: 0;
  border: none;
}

/* 이미지 뷰어 스타일 */
.simple-image-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: transparent; /* 배경색 제거 */
  padding: 0; /* 패딩 제거 */
  border-radius: 0; /* 경계선 제거 */
  box-shadow: none; /* 그림자 제거 */
}

/* 부유하는 편집 버튼 */
.floating-edit-button {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 10;
  opacity: 0.9;
  transition: opacity 0.2s;
}

.floating-edit-button:hover {
  opacity: 1;
}

/* 불필요한 스타일 제거 - iframe 내부에 이미 저장 버튼이
   존재하므로 외부에 추가 버튼 없음 */

.main-preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block; /* 인라인 간격 제거 */
  background-color: white; /* 이미지 배경을 흰색으로 설정하여 확실히 구분 */
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05); /* 약간의 그림자 추가 */
  border: 1px solid #eee; /* 경계선 추가 */
}

.no-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #aaa;
  font-size: 14px;
}

.no-image-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

/* 중복된 글자 삭제 버튼 제거로 인해 관련 스타일도 제거 */

/* 로딩 상태 */
.loading-container {
  text-align: center;
  padding: 40px 0;
}

/* 로딩 오버레이 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.loading-content {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.loading-content p {
  margin-top: 15px;
  color: #333;
}

/* 텍스트 제거 배지 스타일 */
.text-removed-badge {
  background-color: #28a745;
  color: white;
  padding: 0 3px;
  border-radius: 3px;
  font-size: 9px;
  display: inline-block;
}

/* 현재 메인 이미지 미리보기 스타일 */
.current-image-preview {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 10px;
  background-color: #f9f9f9;
}

.square-image-container {
  position: relative;
  width: 100%;
  padding-bottom: 100%; /* 1:1 비율 유지 (정사각형) */
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.square-preview-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* 이미지를 잘라서 컨테이너를 채움 */
}

.square-image-container:hover {
  opacity: 0.9;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
}

.square-image-container:hover .preview-tooltip {
  opacity: 1;
}

.preview-tooltip {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  padding: 4px;
  text-align: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.small-placeholder {
  color: #aaa;
  font-size: 12px;
}

/* 전체 화면 미리보기 스타일 */
.fullscreen-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.fullscreen-preview-container {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.fullscreen-preview-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.close-fullscreen-btn {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.close-fullscreen-btn:hover {
  background: rgba(255, 255, 255, 0.4);
}

/* 에디터 에러 메시지 스타일 */
.editor-error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  text-align: center;
  color: #555;
}

.editor-error-message i {
  font-size: 48px;
  color: #dc3545;
  margin-bottom: 20px;
}

.editor-error-message h5 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.editor-error-message p {
  color: #777;
  margin-bottom: 15px;
}

/* 반응형 조정 */
@media (max-width: 768px) {
  .image-editor-layout {
    flex-direction: column;
  }

  .image-selector-panel {
    width: 100%;
    max-height: 200px;
    border-right: none;
    border-bottom: 1px solid #eee;
  }
}

/* 미리보기 모드 스타일 수정 */
.editor-header {
  padding: 10px 15px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  /* 추가: position 속성 */
}

.mode-toggle {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  /* 스위치가 다른 요소 위에 표시되도록 */
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-container {
  width: 80%;
  height: 80%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border: 2px solid white;
}

.no-image-placeholder {
  color: white;
  text-align: center;
}

.no-image-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

.close-preview-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  border: none;
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.close-preview-btn:hover {
  background-color: rgba(255, 255, 255, 0.5);
}

.close-preview-btn:focus {
  outline: none;
}
</style>